import {tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import type {VideoHeroFragment} from '@/graphql-types'
import {useSignupModal} from '@/providers/signup-modal'
import {getVideoSources} from '@/utils/get-video-sources'
import Button from '@helios/shared/components/button'
import PrismicLink from '@helios/shared/components/prismic-link'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import {Video} from '@helios/shared/components/video'
import {Section} from '@helios/shared/styles/grid'
import getEnvironmentFormId from '@helios/shared/utils/get-environment-field'
import mq from '@helios/shared/utils/media-query'

interface Props {
    slice: VideoHeroFragment
}

export const VideoHero = React.forwardRef(({slice}: Props, ref) => {
    const {renderSignupModal} = useSignupModal()

    const content = slice.primary
    const title = content.title
    const body = content.lead_text.richText
    const predefinedSignupBanner = content.predefined_signup_banner.document
    const submitText = content.signup_label ?? predefinedSignupBanner.data.submit_text
    const video = getVideoSources({
        webm: content.video_webm,
        mp4: content.video_mp4,
    })
    const formId = getEnvironmentFormId(
        predefinedSignupBanner.data.hubspot_form_id_production,
        predefinedSignupBanner.data.hubspot_form_id_staging
    )

    const openSignupModal = () => {
        renderSignupModal({
            formId: formId,
            image: content.signup_modal_image,
            title: predefinedSignupBanner.data.title,
            subtitle: predefinedSignupBanner.data.subtitle,
            highlightedFeatures: [],
        })
    }

    return (
        <>
            <Section ref={ref}>
                <Content>
                    {hasRichText(title) && (
                        <Title forwardedAs="h1" variant="heading-large" content={title} />
                    )}
                    {hasRichText(body) && (
                        <LeadText color="muted" variant="paragraph-large" content={body} />
                    )}
                    <ButtonWrapper>
                        <StyledButton
                            data-testid="homepage-hero-signup-cta"
                            onClick={openSignupModal}
                        >
                            {submitText}
                        </StyledButton>
                        <StyledButton
                            forwardedAs={PrismicLink}
                            {...content.cta_link}
                            variant="outline"
                        >
                            {content.cta_text}
                        </StyledButton>
                    </ButtonWrapper>
                </Content>
                {video && (
                    <VideoWrapper>
                        <Video video={video} poster={content.video_poster.url} />
                    </VideoWrapper>
                )}
            </Section>
        </>
    )
})

const Content = styled.div`
    margin: 0 auto;

    ${mq.max('sm')} {
        padding-bottom: ${tokens.spacing40};
    }
`

const Title = styled(RichText)`
    ${mq.min('sm')} {
        text-align: center;
    }
`

const LeadText = styled(RichText)`
    margin: ${tokens.spacing12} auto 0;
    max-width: 840px;

    ${mq.min('sm')} {
        margin-top: ${tokens.spacing20};
        text-align: center;
    }
`

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: ${tokens.spacing12};
    padding: ${tokens.spacing20} 0;

    ${mq.min('sm')} {
        padding: ${tokens.spacing40} 0;
        gap: ${tokens.spacing20};
        flex-wrap: nowrap;
    }

    ${mq.min('md')} {
        padding-bottom: 80px;
    }
`

const StyledButton = styled(Button)`
    ${mq.max('sm')} {
        width: 100%;
    }
`

const VideoWrapper = styled.div`
    margin: ${tokens.spacing20} auto 0;
    max-width: 1040px;

    ${mq.min('sm')} {
        margin-top: ${tokens.spacing40};
    }
`
